import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

/**
 * One avatar size app-wide: 24px with a 12px letter, as the workflow card
 * design specifies. Sizes only ever differed here because each surface was
 * built at a different time, so callers should not override this.
 */
const AVATAR = 'size-6 shrink-0';
const AVATAR_LETTER = 'rounded-full text-xs font-medium';

/** Avatars past this many collapse into a single `+N` bubble. */
const MAX_STACKED_AVATARS = 3;

interface UserAvatarProps {
  email?: string | null;
  /** Shown in place of the email's first letter when we know the person's name. */
  name?: string | null;
  /** Framing for the avatar, e.g. the ring that separates a stacked face. */
  className?: string;
  title?: string;
}

/**
 * Avatar for someone we know only by email. The hue is derived from the email
 * so the same person keeps the same color on every surface that shows them.
 */
export function UserAvatar({ email, name, className, title }: UserAvatarProps) {
  const hue = ((email ?? name ?? '').length * 37) % 360;

  return (
    <Avatar className={cn(AVATAR, className)} title={title}>
      <AvatarFallback
        className={AVATAR_LETTER}
        style={{
          backgroundColor: `hsl(${hue}, 60%, 45%)`,
          color: 'white',
        }}
      >
        {(name || email || '?').charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

interface UserAvatarGroupProps {
  emails: string[];
  /** How many faces to show before collapsing the rest into `+N`. */
  max?: number;
  className?: string;
  /** Framing applied to every avatar in the stack. */
  avatarClassName?: string;
}

/**
 * Overlapping stack of the people on something, trailing a `+N` bubble once
 * there are more of them than the stack shows.
 */
export function UserAvatarGroup({
  emails,
  max = MAX_STACKED_AVATARS,
  className,
  avatarClassName,
}: UserAvatarGroupProps) {
  if (emails.length === 0) return null;

  const shown = emails.slice(0, max);
  const hidden = emails.slice(shown.length);

  return (
    <div className={cn('flex shrink-0 -space-x-2', className)}>
      {shown.map((email) => (
        <UserAvatar
          key={email}
          email={email}
          title={email}
          className={avatarClassName}
        />
      ))}
      {hidden.length > 0 && (
        <Avatar
          className={cn(AVATAR, avatarClassName)}
          title={hidden.join(', ')}
        >
          <AvatarFallback className={cn(AVATAR_LETTER, 'bg-muted')}>
            +{hidden.length}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
}
